import classNames from 'classnames/bind';
import React, { forwardRef, useEffect, useState } from 'react';
import Checkbox from '../Checkbox';
import TreeSelect, { TreeNodeModel } from '../TreeSelect';
import { TreeSelectHandle } from '../TreeSelect/TreeSelect';

import styles from './FilterBox.module.scss';

const cx = classNames.bind(styles);

interface FilterProps {
  data: TreeNodeModel[];
  placeholder: string;
  dataTestId?: string;
  showSearch?: boolean;
  onChange: (checkedKeys: string[]) => void;
  checkedKeys: string[];
  disabled?: boolean;
}

const getFilterKeys = (data: TreeNodeModel): string[] => {
  if (!data.children) {
    return [data.key];
  }
  const childrenTagIds = data.children.flatMap((node) => getFilterKeys(node));
  return [data.key, ...childrenTagIds];
};

const FilterBox = (
  { data, placeholder, dataTestId, showSearch, onChange, checkedKeys, disabled }: FilterProps,
  ref
): React.ReactElement => {
  const dataKeys = data.flatMap((filter) => getFilterKeys(filter));
  const validSelectedFilterKeys = Array.from(new Set(checkedKeys.filter((key) => dataKeys.includes(key))));

  const [allChecked, setAllChecked] = useState(false);

  const checkAll = (isAll: boolean) => {
    if (!isAll) {
      onChange([]);
    } else {
      onChange(dataKeys);
    }
    setAllChecked(isAll);
  };

  useEffect(() => {
    if (validSelectedFilterKeys.length !== dataKeys.length && allChecked) {
      setAllChecked(false);
    }
    if (validSelectedFilterKeys.length === dataKeys.length && !allChecked) {
      setAllChecked(true);
    }
  }, [validSelectedFilterKeys, allChecked]);

  return (
    <div className={cx('body')} data-testid={dataTestId}>
      <div className={cx('header')}>
        <span className={cx('placeholder', disabled && 'disabled')}>{placeholder}</span>
        <Checkbox
          dataTestId="filter-toggle"
          toggle
          label="All"
          aria-label="All"
          onChange={() => checkAll(!allChecked)}
          checked={allChecked}
          disabled={disabled}
        />
      </div>
      <TreeSelect
        plainTheme
        treeHeight={168}
        showReset={false}
        showSearch={showSearch}
        inputAutoFocus={false}
        data={data}
        checkedKeys={validSelectedFilterKeys}
        setCheckedKeys={(validCheckedKeys) => {
          onChange(validCheckedKeys);
        }}
        ref={ref}
        disabled={disabled}
      />
    </div>
  );
};

export default forwardRef<TreeSelectHandle, FilterProps>(FilterBox);
